import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button, TextField, FormControl, Typography, Box, Divider, Tooltip,
  InputLabel, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Grid, Hidden, LinearProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'material-ui-snackbar-provider';
import EmailPreview from '../email/EmailPreview';
import { placeholders } from '../placeholders';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from 'ckeditor5-custom-build';
import { getToken } from '../../service/authentication';
import { DEPLOYMENT_URL } from '../../config';
import { useSelector } from 'react-redux';

import {
  useGetEmailTemplatesByIdQuery,
  useAddEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation
} from '../../apis/backend';


const useStyles = makeStyles(theme => ({
  root: {
    fontSize: 18,
  },
  formContainer: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
    },
    'flex': 1,
  },
  buttonContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    '& .MuiButton-root': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  },
  previewSeparator: {
    marginLeft: theme.spacing(5),
    marginRight: theme.spacing(5)
  },
  previewContainer: {
    'flex': 1,
  },
  actionButtons: {
    whiteSpace: 'nowrap',
    '& .MuiButton-root': {
      marginRight: 10,
    }
  }
}));

const EmailForm = () => {
  const chainId = useSelector(state => state.doris.selectedChain);
  const siteId = useSelector(state => state.doris.selectedSite);
  const [emailTemplateForm, setEmailTemplateForm] = useState({ title: '', message_content: '' });
  const [editorData, setEditorData] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [skipGetEmailTemplateById, setSkipGetEmailTemplateById] = useState(true);
  const [editor, setEditor] = useState();
  const [token, setToken] = useState('');

  const { id } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const snackbar = useSnackbar();

  const { data: emailTemplate, isLoading, error } = useGetEmailTemplatesByIdQuery(id, { skip: skipGetEmailTemplateById });
  const [addEmailTemplate] = useAddEmailTemplateMutation();
  const [updateEmailTemplate] = useUpdateEmailTemplateMutation();
  const [deleteEmailTemplate] = useDeleteEmailTemplateMutation();

  useEffect(() => {
    const chainAndTemplate = emailTemplate && chainId && siteId;
    if (!chainAndTemplate) return;
    const templateBelongsToChain = emailTemplate.chain_id === parseInt(chainId);
    const templateBelongsToSite = emailTemplate.site_id === parseInt(siteId);
    if (!templateBelongsToChain || !templateBelongsToSite) history.push('/templates/email');
  }, [chainId, siteId]);

  const assignToken = async () => {
    const token = await getToken();
    setToken(token);
  };

  useEffect(() => {
    assignToken();
  }, []);

  useEffect(() => {
    if (!id) return;
    setSkipGetEmailTemplateById(false);
  }, [id]);

  useEffect(() => {
    setEmailTemplateForm(emailTemplate);
    setEditorData(emailTemplate?.message_content)
  }, [emailTemplate]);

  useEffect(() => {
    setEmailTemplateForm({ ...emailTemplateForm, message_content: editorData})
  }, [editorData]);

  const handleInputChange = event => {
    setEmailTemplateForm({ ...emailTemplateForm, [event.target.name]: event.target.value });
  };

  const handleSaveButton = async (event) => {
    if (event.currentTarget.form.reportValidity()) {
      const templateIsNew = !parseInt(id);
      let result;
      try {
        if (templateIsNew) {
          result = await addEmailTemplate({ payload: emailTemplateForm, chainId, siteId }).unwrap();
          snackbar.showMessage(t('email-template-create-success', { template: result }));
        } else {
          result = await updateEmailTemplate({ ...emailTemplate, ...emailTemplateForm }).unwrap();
          snackbar.showMessage(t('email-template-update-success', { template: result }));
        }
        history.push({ pathname: '/templates/email' });
      } catch (error) {
        templateIsNew
          ? snackbar.showMessage(t('email-template-create-fail',))
          : snackbar.showMessage(t('email-template-update-fail', { template: result }));
      }
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = async (confirmDelete) => {
    setOpenDialog(false);
    if (confirmDelete) {
      try {
        await deleteEmailTemplate(emailTemplate.id).unwrap();
        snackbar.showMessage(t('email-template-delete-success', { template: emailTemplate }));
        history.push({ pathname: '/templates/email' });
      } catch (error) {
        snackbar.showMessage(t('email-template-delete-fail', { template: emailTemplate }));
      }
    }
  };

  const { t } = useTranslation();

  const editorConfig = {
    toolbar: {
      items: [
        'undo',
        'redo',
        '|',
        'heading',
        {
          label: 'Font',
          icon: 'text',
          items: [
            'fontFamily',
            'fontSize',
            'fontColor'
          ]
        },
        'bold',
        'italic',
        'blockQuote',
        'alignment',
        'indent',
        'outdent',
        {
          label: 'list',
          icon: '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M7 5.75c0 .414.336.75.75.75h9.5a.75.75 0 1 0 0-1.5h-9.5a.75.75 0 0 0-.75.75zm-6 0C1 4.784 1.777 4 2.75 4c.966 0 1.75.777 1.75 1.75 0 .966-.777 1.75-1.75 1.75C1.784 7.5 1 6.723 1 5.75zm6 9c0 .414.336.75.75.75h9.5a.75.75 0 1 0 0-1.5h-9.5a.75.75 0 0 0-.75.75zm-6 0c0-.966.777-1.75 1.75-1.75.966 0 1.75.777 1.75 1.75 0 .966-.777 1.75-1.75 1.75-.966 0-1.75-.777-1.75-1.75z"/></svg>',
          items: [
            'bulletedList',
            'numberedList',
          ],
        },
        'link',
        'insertTable',
        'insertImage',
        {
          label: 'placeholders',
          icon: '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 10V8h-4V4h-2v4h-4V4H8v4H4v2h4v4H4v2h4v4h2v-4h4v4h2v-4h4v-2h-4v-4h4zm-6 4h-4v-4h4v4z"></path></svg>',
          items: [
            'placeholder:registerNumber',
            'placeholder:inspectionDueDate',
            'placeholder:customerName',
            'placeholder:campaignCode',
            'placeholder:campaignEndDate',
            'placeholder:campaignPrice',
            'placeholder:campaignOnlinePrice',
            'placeholder:reservationLink'
          ]
        }
      ],
      shouldNotGroupWhenFull: true,
    },
    image: {
      toolbar: [
        'imageResize',
        '|',
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side',
        '|',
        'imageStyle:alignLeft',
        'imageStyle:alignRight',
        '|',
        'imageStyle:alignBlockLeft',
        'imageStyle:alignCenter',
        'imageStyle:alignBlockRight',
        '|',
        'toggleImageCaption',
        'imageTextAlternative'
      ]
    },
    simpleUpload: {
      uploadUrl: `${DEPLOYMENT_URL}/api/upload?chainId=${chainId}&siteId=${siteId}`,
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${token}`
      }
    },
    fontFamily: {
      supportAllValues: true
    }
  };
  if (!token) return '';
  const emailForm = (
    <div className={classes.root}>

      <Box mb={3}>
        <Typography variant="h4">
          {emailTemplate ? emailTemplate.title : t('add-email-template')}
        </Typography>
        <Divider />
        {isLoading && <LinearProgress />}
      </Box>

      <Grid container>
        <Grid xs={12} lg={6} item>

          <form className={classes.formContainer} autoComplete="off">
            <FormControl fullWidth={true} margin='none'>
              <TextField
                margin="dense"
                required
                variant="outlined"
                name="title"
                label={t('title')}
                value={emailTemplateForm?.title ? emailTemplateForm.title : ''}
                onChange={handleInputChange} />
            </FormControl>

            <Box my={1} p={1}>
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={editorData}
                onReady={editor => {
                  setEditor(editor);
                  editor.setData(emailTemplate?.message_content || '');
                }}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setEditorData(data);
                }} />
            </Box>

            <Hidden lgUp>
              <Box className={classes.previewContainer} p={2}>
                <EmailPreview className={classes.previewContainer} previewText={emailTemplateForm && emailTemplateForm.message_content} />
              </Box>
            </Hidden>

            <Box className={classes.actionButtons} p={1}>
              <Button
                className={classes.button}
                variant="contained"
                color="primary"
                onClick={(event) => handleSaveButton(event)}>
                {t('save-exit')}
              </Button>

              <Button
                className={classes.button}
                variant="outlined"
                color="primary"
                onClick={() => history.push({ pathname: '/templates/email' })
                }>{t('cancel')}
              </Button>

              {
                emailTemplate && (
                  <Button onClick={() => handleOpenDialog()}
                    color="primary"
                    className={classes.button}
                    variant="outlined" >
                    {t('delete')} {t('email-template')}
                  </Button>
                )
              }
            </Box>
          </form>

        </Grid>

        <Hidden mdDown>
          <Grid md={6} item>
            <Box display="flex" height="100%" pr={2}>
              <Divider orientation="vertical" flexItem className={classes.previewSeparator} />
              <Box className={classes.previewContainer}>
                <EmailPreview className={classes.previewContainer} previewText={emailTemplateForm && emailTemplateForm.message_content} />
              </Box>
            </Box>
          </Grid>
        </Hidden>
      </Grid>
    </div>
  );

  if (error) return <Typography>{t('error-load-emailtemplate')}</Typography>;

  return (
    <>
      <Box p={4}>{emailForm}</Box>

      <Dialog
        open={openDialog}
        onClose={() => handleCloseDialog(false)}>
        <DialogTitle
          id="alert-dialog-title">
          {t('delete-email-template')}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            id="alert-dialog-description">
            {t('delete-alert')}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => handleCloseDialog(false)}
            color="primary">
            {t('cancel')}
          </Button>

          <Button
            onClick={() => handleCloseDialog(true)}
            color="primary"
            autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


export default EmailForm;
